import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styles from './MicrositeEmailTemplateEditor.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, RefreshCw } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import TextInput from '../components/TextInput/TextInput'
import MicrositeMergeVariableHints from '../components/MicrositeMergeVariableHints/MicrositeMergeVariableHints'
import MicrositeCodeEditor from '../components/MicrositeCodeEditor/MicrositeCodeEditor'
import MicrositeEmailPreviewPane from '../components/MicrositeEmailPreviewPane/MicrositeEmailPreviewPane'
import ToggleSwitch from '../components/ToggleSwitch/ToggleSwitch'
import MicrositeDeleteConfirm from '../components/MicrositeDeleteConfirm/MicrositeDeleteConfirm'

const AVAILABLE_VARIABLES = ['customerName', 'storeName', 'appointmentDate', 'serviceName']

const EMPTY_FORM = {
    templateName: '',
    subject: '',
    bodyHtml: '',
    isActive: true,
    isSystem: false
}

const MicrositeEmailTemplateEditor = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const isNew = id === 'new' || !id

    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(!isNew)
    const [saving, setSaving] = useState(false)
    const [toast, setToast] = useState(null)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

    const [form, setForm] = useState(EMPTY_FORM)
    const [original, setOriginal] = useState(EMPTY_FORM)

    const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original])

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: activeStore?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    // Note: there's no dedicated "get one email template" API in
    // the current contract — fetching the list and matching by ID
    // client-side, same pattern as the SEO page and popup editors.
    const loadExisting = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetEmailTemplates')
        if (res?.status === 'success') {
            const match = (res.response?.data ?? []).find((r) => String(r.ID) === String(id))
            if (match) {
                const next = {
                    templateName: match.templateName || '',
                    subject: match.subject || '',
                    bodyHtml: match.bodyHtml || '',
                    isActive: match.isActive ?? true,
                    isSystem: !!match.isSystem
                }
                setForm(next)
                setOriginal(next)
            } else {
                showToast('Could not find this template', 'error')
            }
        } else {
            showToast('Could not load template. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post, id])

    useEffect(() => {
        if (!isNew && activeStore?.storeIdentifier) loadExisting()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, id])

    const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

    const handleInsertVariable = (variable) => {
        update('subject', `${form.subject}{{${variable}}}`)
    }

    const handleSave = async () => {
        if (!form.templateName.trim() || !form.subject.trim()) {
            showToast('Template name and subject are required', 'error')
            return
        }
        setSaving(true)

        const payload = {
            templateName: form.templateName,
            subject: form.subject,
            bodyHtml: form.bodyHtml,
            isActive: form.isActive
        }

        const res = isNew
            ? await post('micrositeCreateEmailTemplate', payload)
            : await post('micrositeUpdateEmailTemplate', { ID: id, ...payload })

        setSaving(false)

        if (res?.status === 'success') {
            showToast('Template saved successfully')
            setOriginal(form)
            navigate('/microsite/email-templates')
        } else {
            showToast('Could not save template. Please try again.', 'error')
        }
    }

    const handleDelete = async () => {
        const res = await post('micrositeDeleteEmailTemplate', { ID: id })
        if (res?.status === 'success') {
            showToast('Template deleted')
            navigate('/microsite/email-templates')
        } else {
            showToast('Could not delete template', 'error')
        }
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading template…</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title={isNew ? 'New email template' : 'Edit email template'}
                backTo="/microsite/email-templates"
                backLabel="Email templates"
                isDirty={isDirty}
                saving={saving}
                onSave={handleSave}
                onDelete={!isNew && !form.isSystem ? () => setDeleteConfirmOpen(true) : undefined}
            />

            {form.isSystem && (
                <div className={styles.systemNote}>
                    This is a system template used for automated emails. You can edit its content, but it can't be deleted.
                </div>
            )}

            <div className={styles.layout}>
                <div className={styles.formCol}>
                    <section className={styles.fieldGroup}>
                        <TextInput
                            label="Template name"
                            value={form.templateName}
                            onChange={(v) => update('templateName', v)}
                            placeholder="e.g. Booking Confirmation"
                            required
                            disabled={form.isSystem}
                        />

                        <MicrositeMergeVariableHints
                            variables={AVAILABLE_VARIABLES}
                            onInsert={handleInsertVariable}
                        />

                        <TextInput
                            label="Subject line"
                            value={form.subject}
                            onChange={(v) => update('subject', v)}
                            placeholder="Your appointment at {{storeName}} is confirmed"
                            required
                        />

                        <MicrositeCodeEditor
                            label="Email body (HTML)"
                            mode="html"
                            value={form.bodyHtml}
                            onChange={(v) => update('bodyHtml', v)}
                            rows={14}
                            placeholder="<p>Hi {{customerName}}, your booking is confirmed for {{appointmentDate}}.</p>"
                        />

                        <ToggleSwitch
                            label="Active"
                            description="This template will be used when its triggering event occurs"
                            checked={form.isActive}
                            onChange={(v) => update('isActive', v)}
                        />
                    </section>
                </div>

                <div className={styles.previewCol}>
                    <div className={styles.previewSticky}>
                        <MicrositeEmailPreviewPane subject={form.subject} bodyHtml={form.bodyHtml} />
                    </div>
                </div>
            </div>

            <MicrositeDeleteConfirm
                mode="modal"
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={handleDelete}
                itemLabel="this email template"
            />
        </div>
    )
}

export default MicrositeEmailTemplateEditor